import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { PetService } from '../../../services/pet.service';
import { HistoryModel, PrescriptionsModel } from '../../../models/history.model';
import { CatalogService } from '../../../services/catalog.service';
import { ITreeOptions, TreeModel, TreeNode } from '@circlon/angular-tree-component';
import { Subject } from 'rxjs';
import { SetupService } from '../../../services/setup.service';
import { PetNoteModel } from '../../../models/pet-note.model';
import { SetupDrugLabelModel } from '../../../models/setup-drug-label.model';
import { ProcedureService } from '../../../services/procedure.service';
import { BuildTransactModel } from '../../../models/build-transact.model';
import { TranRecordModel } from '../../../models/tran-record.model';
import { ClientService } from '../../../services/client.service';
import { ManagerialService } from '../../../services/managerial.service';
import { TempPrescModel } from '../../../models/invoice-model';
import { FilePrescModel } from '../../../models/file-presc.model';
import { CurrentUserModel } from "../../../models/current-user.model";
import Swal from 'sweetalert2';
import * as _ from 'lodash';

declare var $: any;

@Component({
  selector: 'ngx-work-history',
  templateUrl: './history.component.html',
  styleUrls: ['history.component.scss'],
})
export class HistoryComponent implements OnInit, OnDestroy {

  private _pet_no: number;
  info = true;
  physical = false;
  parameters = false;
  treatments = false;
  show_detail = false;
  selectedTreeList = [];
  selectedTreeList1 = [];
  selectedTreeList2 = [];
  selectedTreeList3 = [];
  id_filter = 0;
  code = '';
  category = '';
  subcategory = '';
  filtering = false;
  filtering_presc = false;
  description = '';
  selectedPrescription: PrescriptionsModel | null = null;

  @Input() set pet_no(value: number) {
    this._pet_no = value;
    
    // Update the current pet number in the service before making the API call
    this.petService.current_pet_no = value;
    
    this.filtering = false;
    this.filtering_presc = false;
    this.petService.histories_page_number = 1;
    this.petService.getHistoriesByPetId(value)
      .then(() => {
      });
  }
 
  get pet_no(): number {
    return this._pet_no;
  }

  constructor(
    public petService: PetService,
    public catalogService: CatalogService,
    public setupService: SetupService,
    public procedureService: ProcedureService,
    public clientService: ClientService,
    public managerialService: ManagerialService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit() {
    // Load clinic details for prescription reports
    this.loadClinicDetails();
    
    this.petService.changePet
      .subscribe(message => {
        if (message !== this.pet_no) {
          this.pet_no = message;
        }
      });
      
    // Initialize tree nodes
    this.nodes = [
      {
        name: '',
        children: []
      }
    ];
  }

  private loadClinicDetails() {
    // Get current user's clinic number and load clinic details
    if (localStorage.currentUser && localStorage.token) {
      const currentUser = JSON.parse(localStorage.currentUser);
      const clinicNo = currentUser.clinic_no;
      
      if (clinicNo) {
        this.clientService.GetClinicDetailsByNo(clinicNo).then(() => {
          console.log('Clinic details loaded:', this.clientService.clinic_details);
        }).catch(error => {
          console.error('Error loading clinic details:', error);
        });
      }
    }
  }

  ngOnDestroy(): void {
    // Cancel any pending history requests when component is destroyed
    this.petService.cancelHistoryRequests?.next();
  }

  printPrescription() {
    // Check if a prescription is selected
    if (!this.selectedPrescription) {
      Swal.fire('No prescription selected', 'Please double-click on a prescription row to select it, then click Print', 'warning');
      return;
    }

    // Get current pet and client data
    const currentPet = this.petService.pet_detail;
    const currentClient = this.clientService.client;

    // Create the print content
    const printContent = this.generatePrescriptionReport(this.selectedPrescription, currentPet, currentClient);
    
    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  }

  private generatePrescriptionReport(prescription: PrescriptionsModel, pet: any, client: any): string {
    // Calculate pet age
    const calculateAge = (dob: string) => {
      if (!dob) return '';
      const birthDate = new Date(dob);
      const today = new Date();
      const ageInMs = today.getTime() - birthDate.getTime();
      const ageInYears = Math.floor(ageInMs / (1000 * 60 * 60 * 24 * 365.25));
      const ageInMonths = Math.floor((ageInMs % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44));
      return `${ageInYears} Years ${ageInMonths} Months`;
    };

    const petAge = calculateAge(pet.dob);
    const prescriptionDate = prescription.drug_note_date || new Date().toLocaleDateString();
    
    // Get clinic information from client service
    const clinicName = this.clientService.clinic_details?.clinic_name || 'Family PetCare';
    const clinicAddress = this.clientService.clinic_details?.clinic_address || '1501 Handley Dr';
    const clinicCity = this.clientService.clinic_details?.city || 'FORT WORTH';
    const clinicState = this.clientService.clinic_details?.state || 'TX';
    const clinicZip = this.clientService.clinic_details?.clinic_zip || '76112';
    const clinicPhone = this.clientService.clinic_details?.clinic_phone || '817-451-7387';
    
    const fullAddress = `${clinicAddress}, ${clinicCity}, ${clinicState} ${clinicZip}`;
    
    return `
      <!DOCTYPE html>
      <html>
      <title>Prescription Report</title>
      <head>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            margin: 20px;
            background: white;
          }
          .header {
            text-align: left;
            margin-bottom: 20px;
            border-bottom: 1px solid #000;
            padding-bottom: 10px;
          }
          .facility-info {
            margin-bottom: 15px;
          }
          .section {
            margin-bottom: 15px;
          }
          .section-title {
            font-weight: bold;
            margin-bottom: 5px;
          }
          .info-row {
            margin-bottom: 3px;
          }
          .label {
            font-weight: bold;
            display: inline-block;
            min-width: 120px;
          }
          .value {
            display: inline-block;
          }
          .prescription-details {
            margin-top: 20px;
            border-top: 1px solid #000;
            padding-top: 10px;
          }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <br>
        <div class="header">
          <div class="facility-info">
            <div class="info-row">
              <span class="value">${clinicName}</span>
            </div>
            <div class="info-row">
              <span class="value">${fullAddress}</span>
            </div>
            <div class="info-row">
              <span class="value">${clinicPhone}</span>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Client Information:</div>
          <div class="info-row">
            <span class="label">First Name:</span>
            <span class="value">${client.first_name || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Last Name:</span>
            <span class="value">${client.last_name || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Address:</span>
            <span class="value">${client.address || ''}, ${client.city || ''}, ${client.state || ''} ${client.zip || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Phone:</span>
            <span class="value">${client.home_phone || ''}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Patient (Pet) Information:</div>
          <div class="info-row">
            <span class="label">Name:</span>
            <span class="value">${pet.pet_name || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Species:</span>
            <span class="value">${pet.specie_name || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Date of Birth:</span>
            <span class="value">${pet.dob ? new Date(pet.dob).toLocaleDateString() : ''} (estimated age: ${petAge || ''})</span>
          </div>
        </div>

        <div class="prescription-details">
          <div class="section-title">Prescription/Product Details:</div>
          <div class="info-row">
            <span class="label">Date of Record/Prescription:</span>
            <span class="value">${prescriptionDate}</span>
          </div>
          <div class="info-row">
            <span class="label">Product Name:</span>
            <span class="value">${prescription.product_name || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Count:</span>
            <span class="value">${prescription.units || ''} ${prescription.package || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Usage Directions:</span>
            <span class="value">${prescription.instructions || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Doctor:</span>
            <span class="value">${prescription.employee_initial || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Refills:</span>
            <span class="value">${prescription.refill_count || ''}</span>
          </div>
          <div class="info-row">
            <span class="label">Refill Expiry:</span>
            <span class="value">${prescription.refill_expire || ''}</span>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  add() {
    $('#modalNewPetNote').modal('toggle')
  }

  edit(item) {
    this.petService.pet_note = item;
    $('#modalNewPetNote').modal('toggle')
  }

  ver(tipo: string) {
    this.info = tipo === 'info'
    this.physical = tipo === 'physical'
    this.parameters = tipo === 'parameters'
    this.treatments = tipo === 'treatments'
    
    // Clear prescription selection when switching tabs
    this.selectedPrescription = null;
  }

  chgCategory() {
    if (String(this.category) === '') {
      this.selectedTreeList3 = [];
      return;
    }

    let arr = [];
    this.petService.pet_histories.forEach(element => {
      if (element.category_name === this.category && element.subcategory_name)
        arr.push(element.subcategory_name);
    });

    var unique = arr.filter(this.onlyUnique);
    this.selectedTreeList3 = unique.sort((a, b) => (a < b) ? -1 : 1);
  }



  filter(id: number, treeModel: TreeModel) {
    Swal.fire({
      title: 'Loading...',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    let arr = [];
    let arr1 = [];
    let arr2 = [];

    if (this.id_filter !== id) {
      this.nodes = [
        {
          name: '',
          children: []
        }
      ];
      treeModel.nodes[0].children = [];
      this.selectedTreeList = [];
      this.selectedTreeList1 = [];
      this.selectedTreeList2 = [];
      treeModel.update();
      this.id_filter = id;
    }
    this.id_filter = id;
    this.clear(treeModel);

    setTimeout(() => {
      switch (id) {
        case 1:
          if (treeModel.nodes[0].children.length > 0) break;

          this.petService.pet_histories.forEach(element => {
            if (element.procedure_desc)
              arr.push(element.procedure_desc);
            if (element.code_name)
              arr1.push(element.code_name);
            if (element.category_name)
              arr2.push(element.category_name);
          });

          var unique = arr.filter(this.onlyUnique);
          var unique1 = arr1.filter(this.onlyUnique);
          var unique2 = arr2.filter(this.onlyUnique);

          unique.sort((a, b) => (a < b) ? -1 : 1).forEach(element => {
            treeModel.nodes[0].children.push({ id: element, name: element });
          });

          this.selectedTreeList1 = unique1.sort((a, b) => (a < b) ? -1 : 1);
          this.selectedTreeList2 = unique2.sort((a, b) => (a < b) ? -1 : 1);

          treeModel.nodes[0].name = 'Procedures'
          treeModel.update();

          this.petService.pet_histories_filter = [];
          this.petService.pet_histories_filter.push(...this.petService.pet_histories);
          break;

        case 3:
          if (treeModel.nodes[0].children.length > 0) break;

          this.petService.pet_prescriptions.forEach(element => {
            if (element.product_name)
              arr.push(element.product_name);
          }); 

          var unique = arr.filter(this.onlyUnique);

          unique.sort((a, b) => (a < b) ? -1 : 1).forEach(element => {
            treeModel.nodes[0].children.push({ id: element, name: element });
          });

          treeModel.nodes[0].name = 'Prescriptions'
          treeModel.update();

          this.petService.pet_prescriptions_filter = [];
          this.petService.pet_prescriptions_filter.push(...this.petService.pet_prescriptions);
          break;

      }

      $('#modalFilterHistory').modal('toggle');
      treeModel.expandAll();
      Swal.close();
    }, 150);
  }

  onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  clear(treeModel: TreeModel, close: boolean = false) {
    this.filtering = false;
    this.filtering_presc = false;
    this.petService.pet_histories_filter = [];
    this.petService.pet_prescriptions_filter = [];
    this.selectedTreeList = [];
    
    treeModel.getActiveNodes().forEach((element: TreeNode) => {
      element.setIsActive(false);
    });

    
    if (close) 
      $('#modalFilterHistory').modal('toggle');
    
  }

  selected(treeModel: TreeModel) {
    this.selectedTreeList = [];
    
    treeModel.getActiveNodes().forEach(element => {
      this.selectedTreeList.push(element.data.id);
    });
  }

  apply(treeModel) {
    this.selected(treeModel);

    if (this.id_filter === 1) {
      // Filter for History
      if (this.selectedTreeList && this.selectedTreeList.length > 0) {
        this.filtering = true;
        let by_code = []
        by_code.push(..._.filter(this.petService.pet_histories, (row: HistoryModel) => this.selectedTreeList.some(t => t === row.procedure_desc) 
        && row.code_name === (this.code !== '' ? this.code : row.code_name) && row.category_name === (this.category !== '' ? this.category : row.category_name) 
        && row.subcategory_name === (this.subcategory !== '' ? this.subcategory : row.subcategory_name)
        && row.procedure_desc.toLowerCase().includes(this.description !== '' ? this.description.toLowerCase() : row.procedure_desc.toLowerCase())));
        by_code.sort((a, b) => (this.parseToTime1(a.done_date) < this.parseToTime1(b.done_date)) ? -1 : 1);
        this.petService.pet_histories_filter = []
        this.petService.pet_histories_filter.push(...by_code)
      } else {
        // Search by description only or other filters
        if (this.description !== '' || this.code !== '' || this.category !== '' || this.subcategory !== '') {
          this.filtering = true;
          let by_code = []
          by_code.push(..._.filter(this.petService.pet_histories, (row: HistoryModel) => 
            row.code_name === (this.code !== '' ? this.code : row.code_name) 
            && row.category_name === (this.category !== '' ? this.category : row.category_name) 
            && row.subcategory_name === (this.subcategory !== '' ? this.subcategory : row.subcategory_name)
            && row.procedure_desc.toLowerCase().includes(this.description !== '' ? this.description.toLowerCase() : row.procedure_desc.toLowerCase())
          ));
          by_code.sort((a, b) => (this.parseToTime1(a.done_date) < this.parseToTime1(b.done_date)) ? -1 : 1);
          this.petService.pet_histories_filter = []
          this.petService.pet_histories_filter.push(...by_code)
        }
      }
      
    } else if (this.id_filter === 3) {
      // Filter for Prescriptions
      if (this.selectedTreeList && this.selectedTreeList.length > 0) {
        this.filtering_presc = true;
        let by_code = []
        by_code.push(..._.filter(this.petService.pet_prescriptions, (row: PrescriptionsModel) => this.selectedTreeList.some(t => t === row.product_name)         
        && row.instructions.toLowerCase().includes(this.description !== '' ? this.description.toLowerCase() : row.instructions.toLowerCase())));
        by_code.sort((a, b) => (this.parseToTime1(a.drug_note_date) < this.parseToTime1(b.drug_note_date)) ? -1 : 1);
        this.petService.pet_prescriptions_filter = []
        this.petService.pet_prescriptions_filter.push(...by_code)
      } else {
        // Search by instructions only or other filters
        if (this.description !== '' ) {
          this.filtering_presc = true;
          let by_code = []
          by_code.push(..._.filter(this.petService.pet_prescriptions, (row: PrescriptionsModel) => 
            row.instructions.toLowerCase().includes(this.description !== '' ? this.description.toLowerCase() : row.instructions.toLowerCase())         ));
          by_code.sort((a, b) => (this.parseToTime1(a.drug_note_date) < this.parseToTime1(b.drug_note_date)) ? -1 : 1);
          this.petService.pet_prescriptions_filter = []
          this.petService.pet_prescriptions_filter.push(...by_code)
        }
      }
   
    }
    $('#modalFilterHistory').modal('toggle');
    
  }

  parseToTime1(date: string) {
    let split = date.split('/');
    if (split) {
      let date = new Date(Number(split[2]), Number(split[0]) - 1, Number(split[1]));
      return date.getTime()
    }
    return 0;
  }

  onScroll() {
    this.petService.getHistoriesByPetId(this.petService.current_pet_no)
      .then(() => {
      });
  }

  selectPresc(item: PrescriptionsModel) {
 
    this.selectedPrescription = item;
  }

  async updatePresc(item: PrescriptionsModel) {
    //debugger;
    console.log(item);
    let intCodeNo = 0;
    let strDate = '';
    let strTemp = '';
    let intRefill = 0;
    let strNext = '';

   // this.selectedPrescription = item;

    if (item.in_clinic === 'False') {
      Swal.fire({
        title: 'Create another written Rx for this product?',
        showCancelButton: true,
        cancelButtonText: 'No',
        confirmButtonText: 'Yes'
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          await this.writtenRx(item);
        }
      });
      return;
    }

    //debugger;
    await this.setupService.getInventInfo('', item.product_name, 1)
      .then(() => {
        if (this.setupService.invent_info.item_no === '') {
          intCodeNo = Number(this.setupService.invent_info.item_no);
        } else {
          Swal.fire('No item found with this description. Item description may have changed in the database. Please create a new prescription to generate refills.');
          return;
        }
      });

    let lngSeq = Number(item.drug_note_sequence);
    let strDoctor = item.employee_initial;

    // Calculate default refill expiry date
    var refill_expire = '';
    let today_refill = new Date();
    today_refill.setFullYear(today_refill.getFullYear() + 1);
    if (today_refill) {
      var split = today_refill.toLocaleDateString("en-US").split('/');
      refill_expire = `${split[2]}-${split[0].padStart(2, '0')}-${split[1].padStart(2, '0')}`;
    }

    // Single dialog with all 4 questions
    const { value: formValues } = await Swal.fire({
      title: 'Update Prescription',
      customClass: {
        popup: 'swal-wide-popup'
      },
      html: `
        <div style="text-align: left; margin-bottom: 2px;">
          <label style="display: inline-block; min-width: 200px; margin-right: 1px;">- Enter initials of doctor approving refills:</label>
          <input id="swal-input1" class="swal1-input" value="${strDoctor}" placeholder="Enter doctor initials">
        </div>
        <div style="text-align: left; margin-bottom: 2px; display: inline-block;" >
          <label style="display: inline-block; min-width: 200px; margin-right: 1px;">- Please enter number of refills allowed for ${item.product_name}.</label>
          <label style="display: inline-block; min-width: 200px; margin-right: 1px;">&nbsp;&nbsp;&nbsp;Use 'PRN' for unlimited refills:</label>
          <input id="swal-input2" class="swal1-input" value="${item.refill_count}" placeholder="Enter number or 'PRN'">
        </div>
        <div style="text-align: left; margin-bottom: 2px;">
          <label style="display: inline-block; min-width: 200px; margin-right: 1px;">- Please enter refill expiry date:</label>
          <input id="swal-input3" class="swal2-input" type="date" value="${refill_expire}">
        </div>
        <div style="text-align: left; margin-bottom: 2px;">
          <label style="display: inline-block; min-width: 200px; margin-right: 1px;">- Enter date of next allowed refill:</label>
          <input id="swal-input4" class="swal2-input" type="date" value="${refill_expire}">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'OK',
      preConfirm: () => {
        const doctorInitials = (document.getElementById('swal-input1') as HTMLInputElement).value;
        const refillCount = (document.getElementById('swal-input2') as HTMLInputElement).value;
        const refillExpiry = (document.getElementById('swal-input3') as HTMLInputElement).value;
        const nextRefillDate = (document.getElementById('swal-input4') as HTMLInputElement).value;

        if (!doctorInitials || !refillCount || !refillExpiry || !nextRefillDate) {
          Swal.showValidationMessage('Please fill in all fields');
          return false;
        }

        return {
          doctorInitials,
          refillCount,
          refillExpiry,
          nextRefillDate
        };
      }
    });

    if (formValues) {
      strDoctor = formValues.doctorInitials;
      strDate = formValues.refillCount;
      strNext = formValues.nextRefillDate;

      if (strDate === 'PRN') {
        intRefill = -1;
      } else if (strDate === '') {
        return;
      } else {
        intRefill = Number(strDate);
      }

      if (intRefill < -1) {
        Swal.fire('Number of refills must be greater than or equal to zero.');
        return;
      }

      const refillExpiryDate = formValues.refillExpiry;

      // Update drug note
      this.setupService.setup_druglabel = new SetupDrugLabelModel(lngSeq, intRefill, refillExpiryDate, refillExpiryDate, 0, 0, 1);
      await this.setupService.setupDrugNote()
        .then(() => {
          // Update the next refill date in the drug label
          this.setupService.setup_druglabel = new SetupDrugLabelModel(lngSeq, intRefill, refillExpiryDate, strNext, intCodeNo, this.pet_no, 1);
          return this.setupService.setupDrugLabel();
        });

      // Update prescription data
      if (intRefill === -1) {
        item.refill_count = "PRN";
      } else {
        item.refill_count = `${intRefill}`;
      }
      item.refill_expire = refillExpiryDate;

      // Create note
      strTemp += `For ${item.product_name}, ${strDoctor}  approved `;
      if (intRefill === -1) {
        strTemp += `PRN refills`
      } else if (intRefill === 1) {
        strTemp += `1 refill`
      } else {
        strTemp += `${intRefill} refills`
      }

      strTemp += ` until ${refillExpiryDate}.     -- ${'tdm'}`;

      Swal.fire({
        title: 'Loading...',
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      this.petService.pet_note = new PetNoteModel(0, '', this.pet_no, 0, strTemp, '', '');
      var note_sequence = 0;

      this.petService.setupPetNoteByType()
        .then((_note_sequence: number) => {
          note_sequence = _note_sequence;
          let promises = [];
          promises.push(this.petService.setupPetNoteType(new PetNoteModel(note_sequence, '', 0, 2, '', '', '')));
          promises.push(this.petService.setupPetNoteType(new PetNoteModel(note_sequence, '', 0, 25, '', '', '')));

          Promise.all(promises)
            .then(() => {
              this.petService.histories_page_number = 1;
              this.petService.getHistoriesByPetId(this.petService.current_pet_no)
                .then(() => {
                  Swal.fire({
                    title: 'Your information has been saved.',
                    didOpen: () => {
                    },
                  })
                });
            });
        });
    }
  }

  async UpdateDrugNote(lngSeq: number, intRefill: number, strDate: string) {
    this.setupService.setup_druglabel = new SetupDrugLabelModel(lngSeq, intRefill, strDate, strDate, 0, 0, 1);
    await this.setupService.setupDrugNote()
      .then(() => {

      });
  }

  async UpdateDrugLabel(lngSeq: number, intRefill: number, strDate: string, intCodeNo: number, PetNo: number) {
    let strNext = '';

    var refill_expire = '';
    let today_refill = new Date();
    today_refill.setFullYear(today_refill.getFullYear() + 1);
    if (today_refill) {
      // if (item.refill_expire && item.refill_expire !== '') {
      var split = today_refill.toLocaleDateString("en-US").split('/');
      refill_expire = `${split[2]}-${split[0].padStart(2, '0')}-${split[1].padStart(2, '0')}`;
      // }
    }

    const { value: _strNext } = await Swal.fire({
      title: `Enter date of next allowed refill.`,
      input: 'tel',
      inputValue: refill_expire,
      inputAttributes: {
        required: 'true'
      },
      inputValidator: (value) => {
        if (!value) {
          return "You need to write something!";
        }
      }
    });
    if (_strNext) {
      strNext = _strNext;
      // If Not IsDate(strNext) Then strNext = DateAdd("d", 1, Date)
    }

    this.setupService.setup_druglabel = new SetupDrugLabelModel(lngSeq, intRefill, strDate, strDate, intCodeNo, PetNo, 1);
    await this.setupService.setupDrugLabel()
      .then(() => {

      });
  }

  async writtenRx(item: PrescriptionsModel) {
    // debugger;
    let strDoctor = item.employee_initial;
    let intCodeNo = 0;
    let strDate = '';
    let strLine = '';
    let strTemp = '';
    let intRefill = 0;

    const { value: _strDoctor } = await Swal.fire({
      title: "Enter initials of doctor approving refills.",
      input: "text",
      inputValue: strDoctor,
      inputValidator: (value) => {
        if (!value) {
          return "You need to write something!";
        }
      }
    });
    if (_strDoctor) {
      strDoctor = _strDoctor;
    }

    const PrescUpdate = async () => {
      const { value: _strDate } = await Swal.fire({
        title: `Please enter number of refills allowed for ${item.product_name}. Use 'PRN' for unlimited refills.`,
        input: "tel",
        inputValue: item.refill_count,
        inputValidator: (value) => {
          if (!value) {
            return "You need to write something!";
          }
        }
      });
      if (_strDate) {
        strDate = _strDate;
      }

      if (strDate === 'PRN') {
        intRefill = -1;
      } else if (strDate === '') {
        return;
      } else {
        intRefill = Number(strDate);
      }

      if (intRefill < -1) {
        Swal.fire('Number of refills must be greater than or equal to zero.');
        await PrescUpdate();
      }

      var refill_expire = '';
      let today_refill = new Date();
      today_refill.setFullYear(today_refill.getFullYear() + 1);
      if (today_refill) {
        // if (item.refill_expire && item.refill_expire !== '') {
        var split = today_refill.toLocaleDateString("en-US").split('/');
        refill_expire = `${split[2]}-${split[0].padStart(2, '0')}-${split[1].padStart(2, '0')}`;
        // }
      }

      const { value: _strDate1 } = await Swal.fire({
        title: `Please enter refill expiry date.`,
        input: 'date',
        inputValue: refill_expire,
        inputValidator: (value) => {
          if (!value) {
            return "You need to write something!";
          }
        }
      });
      if (_strDate1) {
        strDate = _strDate1;
      }

      let strItemCode = "PRESC";
      let lngClientNo = this.petService.pet.client_no;
      let lngPetNo = this.petService.pet.pet_no;
      // let Trans: TranRecordModel[] = [];
      let intTotalIdx = 0;
      let intTranIdx = 0;
      let blnIsNarc = false;
      let strValues = '';
      let strDeaNo = '';
      let strDpsNo = '';
      let currentUser: CurrentUserModel = JSON.parse(localStorage.currentUser); 
      let clinicNo = currentUser.clinic_no;  

      let PubTrans: TranRecordModel[] = [];
      await this.petService.getProcsByPetId(this.petService.current_pet_no)
        .then(() => {
          PubTrans = this.procedureService.displayTran(this.petService.pet_procs)
        });

      await this.procedureService.buildTransact(new BuildTransactModel(lngClientNo, lngPetNo, 2150, 0, 'D', strDoctor, 1, PubTrans, intTotalIdx, strItemCode, 0, intTranIdx, 'T', 0, 1,""))
        .then((res: BuildTransactModel) => {
          PubTrans = res.Trans;
          intTranIdx = res.intIndex;
        });

      if (intTranIdx < 0) {
        return;
      }

      //FILE
      var strLine = '';
      var strFile = `PRESC${PubTrans[intTranIdx].Seq}.txt`;
      strLine += ``;

      // strLine = "Client's First Name: " & CheckStrNull(rs.Fields("first_name")) & "        Client's Last Name: " & CheckStrNull(rs.Fields("last_name"))
      strLine += `Client's First Name: ${this.clientService.client.first_name}        Client's Last Name: ${this.clientService.client.last_name}\n\n`;
      strLine += `Address: ${this.clientService.client.address}, ${this.clientService.client.city}, ${this.clientService.client.state} ${this.clientService.client.zip}`;
      let strZip4 = this.clientService.client.zip4;
      if (strZip4 !== "")
        strLine += `-${this.clientService.client.zip4}\n\n`;
      else
        strLine += `\n`;

      strLine += ` Phone:  ${this.clientService.client.home_phone}\n\n\n`;

      strLine += `Patient's Name: ${this.petService.pet.pet_name}\n`;
      strLine += `  Specie: ${this.petService.pet.specie_name}\n`;
      strLine += `Date of Birth:  ${this.petService.pet.dob} (${this.petService.pet.age})\n\n\n\n`;
      strLine += `Date: ${this.formatDate()}\n\n`;

      //tblTempPresc
      this.setupService.temp_presc = new TempPrescModel(0, 0, '', '', '', 0, '', 0, '', false, '', false, '');
      this.setupService.temp_presc.transact_sequence = PubTrans[intTranIdx].Seq;
      this.setupService.temp_presc.pet_no = this.pet_no;

      await Swal.fire({
        title: 'Is this a Schedule V-II controlled substance?',
        showCancelButton: true,
        cancelButtonText: 'No',
        confirmButtonText: 'Yes'
      }).then(async (result: any) => {
        blnIsNarc = result.isConfirmed
      });

      let strProduct = item.product_name;
      strValues = '';
      let lngSeq = 0;

      this.setupService.temp_presc.product_name = strProduct;
      this.setupService.temp_presc.package = item.package;
      // lngSeq = 
      this.setupService.temp_presc.units = lngSeq;

      PubTrans[intTranIdx].Unit = 0;

      if (blnIsNarc) {
        const { value: _strTemp } = await Swal.fire({
          title: `Please spell ${item.units} as a word`,
          input: "text",
          inputValue: strDoctor,
          inputValidator: (value) => {
            if (!value) {
              return "You need to write something!";
            }
          }
        });
        if (_strTemp) {
          strTemp = _strTemp;
        }
      }

      strLine += `Product Name: ${strProduct}\n\n`;
      strLine += `Count: ${lngSeq}`;
      if (strTemp !== '')
        strLine += ` (${strTemp})\n`;
      else
        strLine += `\n`;

      strLine += ` ${item.package}\n\n`;
      strTemp = item.use_for;

      if (strTemp !== '')
        strLine += `Use for: ${strTemp}\n`;

      strLine += `\n`;
      strLine += `Usage directions:\n`;

      // Call PrintMemo(grdHistory.TextMatrix(intRow, 6), 52, 1, , f)
      this.setupService.temp_presc.instructions = item.instructions;

      strLine += `\n`;
      strLine += `\n`;
      strLine += `\n`;

      let intTemp = intRefill;

      if ((intTemp = -1) || (intTemp > 0)) {
        strTemp = strDate;
        if (this.isDateValid(strDate)) {
          intTemp = 0;
          strLine += `Number of refills allowed: 0\n`
          strTemp = ``; //Format(0, "Short Date")
        } else {
          if (intTemp === -1) {
            strLine += "PRN refills allowed.\n";
          } else {
            strLine += `Number of refills allowed: ${intTemp}\n`;
          }
          strLine += `  Refill Expiration Date: ${strTemp}\n`;
        }
      } else {
        intTemp = 0;
        strTemp = ''; //Format(0, "Short Date")
        strLine += "No refills allowed.\n"
      }
      strLine += `\n`;

      this.setupService.temp_presc.refill_count = intTemp;
      this.setupService.temp_presc.refill_expire = strTemp;

      await Swal.fire({
        title: 'Dispense as written? Product selection will NOT be permitted.',
        showCancelButton: true,
        cancelButtonText: 'No',
        confirmButtonText: 'Yes'
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          strLine += "Dispense As Written / BRAND NECESSARY\n";
          this.setupService.temp_presc.allow_generic = false;
        } else {
          strLine += "Product selection permitted.\n";
          this.setupService.temp_presc.allow_generic = true;
        }
      });

      strLine += `\n`;
      strLine += `Veterinarian: \n`;

      this.managerialService.search.page_number = 1;
      this.managerialService.search.username = strDoctor;
      await this.managerialService.getEmployeesByParams()
        .then((res) => {
          if (this.managerialService.employees && this.managerialService.employees.length) {
            let rs = this.managerialService.employees[0]
            strTemp = rs.employee_name;
            intTemp = rs.employee_no;
            strDeaNo = rs.doctor_dea_no;
            strDpsNo = rs.doctor_dps_no;
            strZip4 = rs.license_state;
          }
        });

      PubTrans[intTranIdx].ApproveDate = this.formatDate();
      PubTrans[intTranIdx].ApproveBy = intTemp;

      if (strTemp.includes('DVM')) {
        strLine += `${strTemp}\n`;
      } else {
        strLine += `${strTemp}, DVM\n`;
      }

      if (blnIsNarc) {
        if (strDeaNo !== '') {
          strLine += `USDEA registration no.: ${strDeaNo}      ${strZip4}DPS registration no.: ${strDpsNo}\n`;
        } else {
          strLine += `If needed, please call hospital at phone number listed above to verify USDEA and/or${strZip4} DPS registration number of prescribing veterinarian.\n`;
        }
      }
      strLine += `\n`;

      this.setupService.temp_presc.doctor = String(intTemp);
      this.setupService.temp_presc.in_clinic = false;
      this.setupService.temp_presc.use_for = item.use_for === null ? '' : (item.use_for === undefined ? '' : item.use_for);
      this.setupService.temp_presc.presc_date = this.formatDate();

      // Call DB_Update(strLabelSQL)
      PubTrans[intTranIdx].Cost = 0;
      PubTrans[intTranIdx].Unit = 0;
      let strUserName = 'tdm';

      strTemp = "For " + strProduct + ", " + strDoctor + " approved ";
      if (intRefill === -1) {
        strTemp = strTemp + "PRN refills"
      } else if (intRefill === 1) {
        strTemp = strTemp + "1 refill"
      } else {
        strTemp = strTemp + intRefill + " refills "
      }
      strTemp = strTemp + " until " + strDate + ". Written prescription created.    -- " + strUserName;

      Swal.fire({
        title: 'Loading...',
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading()
        },
      })

      console.log(strTemp);
      // debugger;

      this.petService.pet_note = new PetNoteModel(0, '', this.pet_no, 0, strTemp, '', '');
      var note_sequence = 0;

      PubTrans[intTranIdx].Cost = 0;
      PubTrans[intTranIdx].Unit = 0;
      PubTrans[intTranIdx].DoneBy = intTemp;
      PubTrans[intTranIdx].ApproveBy = intTemp;
      PubTrans[intTranIdx].DoneDate = new Date().toISOString().slice(0, 19).replace('T', ' ');;
      PubTrans[intTranIdx].ApproveDate = new Date().toISOString().slice(0, 19).replace('T', ' ');;

      this.procedureService.UpdateTranPresc(PubTrans[intTranIdx])
        .then(() => {
          this.setupService.setupTempPresc()
            .then(() => {
              this.petService.setupPetNoteByType()
                .then((_note_sequence: number) => {
                  note_sequence = _note_sequence;
                  let promises = [];
                  promises.push(this.petService.setupPetNoteType(new PetNoteModel(note_sequence, '', 0, 2, '', '', '')));
                  promises.push(this.petService.setupPetNoteType(new PetNoteModel(note_sequence, '', 0, 25, '', '', '')));

                  Promise.all(promises)
                    .then(() => {
                      this.setupService.setupFilePresc(new FilePrescModel(strLine, strFile, ''))
                        .then((res: any) => {
                          console.log(res.result.url);
                          window.open(res.result.url, '_blank');
                          this.petService.histories_page_number = 1;
                          this.petService.getHistoriesByPetId(this.petService.current_pet_no)
                            .then(() => {
                              Swal.fire({
                                title: 'Your information has been saved.',
                                didOpen: () => {
                                },
                              })
                            });
                        });
                    });
                });
            });
        });

      //frmShowOnScreen

    }
    await PrescUpdate();
  }

  isDateValid(dateStr: string) {
    var timestamp = Date.parse(dateStr);
    return !isNaN(timestamp);
  }

  order(_order: number) {
    this.petService.pet_histories = [];
    this.petService.pet_subhistories = [];
    this.petService.pet_suggestions = [];
    this.petService.pet_prescriptions = [];
    this.petService.histories_order = _order;
    this.petService.histories_page_number = 1;
    this.petService.getHistoriesByPetId(this.petService.current_pet_no)
      .then(() => {
      });
  }

  presOrder(_order: number) {
    this.petService.prescription_order = _order;
  }

  formatDate() {
    var d = new Date(),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2)
      month = '0' + month;
    if (day.length < 2)
      day = '0' + day;

    return [year, month, day].join('-');
  }

  // Handle modal close event
  onModalClose() {
    // Reset filter form when modal is closed
    this.code = '';
    this.category = '';
    this.subcategory = '';
    this.description = '';
    this.selectedTreeList = [];
  }



  nodes = [
    {
      name: 'Procedures',
      children: []
    }
  ];

  nodes1 = [
    {
      name: 'Codes',
      children: []
    }
  ];

  nodes2 = [
    {
      name: 'Categories',
      children: []
    }
  ];

  options: ITreeOptions = {
    useVirtualScroll: false,
    isExpandedField: 'expanded',
    animateExpand: true,
    useCheckbox: true,
    allowDrag: false,
    allowDrop: false,
    levelPadding: 20,
    scrollOnActivate: true,
    scrollContainer: document.documentElement
  };
}
